// @mui material components
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";
import FileUploadIcon from "@mui/icons-material/FileUpload";

// Material Dashboard 2 PRO React TS components
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImportProgressPanel from "./importProgressPanel";
import { MutationLike, useUploadImportJob } from "./hooks/useUploadImportJob";

type UploadFileFormProps = {
  label: string;
  useStartImportJobFunc: () => MutationLike<File, { jobId: string }>;
};

function UploadFileForm({ label, useStartImportJobFunc }: UploadFileFormProps): JSX.Element {
  const {
    disabled,
    isLoading,
    logExpanded,
    setLogExpanded,
    fileInput,
    handleFileChange,
    handleUpdateFile,
    jobId,
    job,
    totalRejected,
    persistedRejections,
    persistedRejectionTotal,
    rejectionsLoading,
    isTimeoutFailure,
    isBlockedByOtherImport,
  } = useUploadImportJob({
    label,
    useStartImportJobFunc,
  });

  return (
    <MDBox bgColor="white" borderRadius="sm" sx={{ boxShadow: 1 }} p={2} my={1}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <MDTypography variant="h5">{label}:</MDTypography>
        </Grid>
        <Grid item xs={12}>
          <MDTypography variant="body2" color="text">
            {fileInput ? fileInput.name : "No file selected"}
          </MDTypography>
          {isBlockedByOtherImport && (
            <MDTypography variant="caption" display="block" color="warning" mt={0.5}>
              Another import is in progress. Only one file can be imported at a time.
            </MDTypography>
          )}
        </Grid>
        <Grid item xs={2}>
          <MDButton
            variant="contained"
            component="label"
            color="info"
            size="small"
            disabled={isLoading}
          >
            Browse&nbsp;
            <input type="file" accept=".xlsx,.xls" hidden onChange={handleFileChange} />
          </MDButton>
        </Grid>
        <Grid item xs={2}>
          <MDButton
            variant="contained"
            color="info"
            size="small"
            onClick={handleUpdateFile}
            disabled={disabled || isLoading}
          >
            {isLoading ? "Starting" : "Start Import"}&nbsp;
            {isLoading ? (
              <CircularProgress size={24} color="secondary" />
            ) : (
              <FileUploadIcon fontSize="large" />
            )}
          </MDButton>
        </Grid>
        <Grid item xs={12}>
          <Accordion expanded={logExpanded} onChange={() => setLogExpanded(!logExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <MDTypography variant="h6">Import Progress</MDTypography>
            </AccordionSummary>
            <AccordionDetails>
              <ImportProgressPanel
                jobId={jobId}
                job={job}
                isTimeoutFailure={isTimeoutFailure}
                totalRejected={totalRejected}
                persistedRejections={persistedRejections}
                persistedRejectionTotal={persistedRejectionTotal}
                rejectionsLoading={rejectionsLoading}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default UploadFileForm;
